import { of, Subscription, timer } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';

import { ConnectorsApi } from '../apis/connectors.api';
import { EnumOAuthAuthorizationStatus } from '../enums/oauth-authorization-status.enum';
import { IAuthorization } from '../interfaces/connection.interface';
import { IOauthResult, IOauthResultSink } from '../interfaces/oauth-result.interface';

const STATUS_POLL_FAST_MS = 2000;
const STATUS_POLL_SLOW_MS = 8000;
const STATUS_POLL_FAST_WINDOW_MS = 30000;
const POPUP_CLOSED_POLL_MS = 800;
const CLOSE_GRACE_MS = 4000;
const MESSAGE_TYPE = 'ceppem.connectors.oauth.result';

interface IOauthMessage {
  type: string;
  version: number;
  status: EnumOAuthAuthorizationStatus;
  /* eslint-disable-next-line @typescript-eslint/naming-convention */
  connection_id: string;
  /* eslint-disable-next-line @typescript-eslint/naming-convention */
  connector_error: string | null;
}

export class PopupSession {
  private connectionId: string | null = null;
  private sink: IOauthResultSink | null = null;
  private statusSub: Subscription | null = null;
  private deadlineSub: Subscription | null = null;
  private closedSub: Subscription | null = null;
  private graceSub: Subscription | null = null;
  private cancelSub: Subscription | null = null;
  private sawPopupOpen = false;
  private settled = false;
  private navigated = false;

  constructor(
    private connectorsApi: ConnectorsApi,
    private popup: Window,
    private apiOrigin: string
  ) {}

  begin(authorization: IAuthorization, sink: IOauthResultSink): void {
    this.connectionId = authorization.connection.id;
    this.sink = sink;
    this.navigated = true;
    window.addEventListener('message', this.onMessage);
    this.startStatusPolling(authorization.expiresAt);
    this.startClosedWatcher();
  }

  closePopup(): void {
    this.popup.close();
  }

  dispose(): void {
    window.removeEventListener('message', this.onMessage);
    this.statusSub?.unsubscribe();
    this.deadlineSub?.unsubscribe();
    this.closedSub?.unsubscribe();
    this.graceSub?.unsubscribe();
    this.cancelSub?.unsubscribe();
    if (!this.navigated) {
      this.closePopup();
    }
  }

  private onMessage = (event: MessageEvent): void => {
    if (event.origin !== this.apiOrigin) {
      return;
    }
    const data = event.data as IOauthMessage;
    if (
      !data ||
      data.type !== MESSAGE_TYPE ||
      data.version !== 1 ||
      data.connection_id !== this.connectionId
    ) {
      return;
    }
    this.settle({
      status: data.status,
      connectionId: data.connection_id,
      connectorError: data.connector_error
    });
  };

  private startStatusPolling(expiresAt: string): void {
    this.pollStatus(0);

    const remaining = new Date(expiresAt).getTime() - Date.now();
    if (Number.isFinite(remaining)) {
      this.deadlineSub = timer(Math.max(remaining, 0)).subscribe(() => this.expire());
    }
  }

  private pollStatus(elapsed: number): void {
    const interval =
      elapsed < STATUS_POLL_FAST_WINDOW_MS ? STATUS_POLL_FAST_MS : STATUS_POLL_SLOW_MS;

    this.statusSub = timer(interval)
      .pipe(
        switchMap(() =>
          this.connectorsApi.getAuthorizationStatus(this.connectionId as string)
        ),
        catchError(() => of(null))
      )
      .subscribe(status => {
        if (this.settled) {
          return;
        }
        if (status && status.terminal) {
          this.settle({
            status: status.status,
            connectionId: status.connectionId,
            connectorError: status.connectorError
          });
          return;
        }
        this.pollStatus(elapsed + interval);
      });
  }

  private expire(): void {
    if (this.settled || !this.connectionId) {
      return;
    }
    this.settle({
      status: EnumOAuthAuthorizationStatus.Expired,
      connectionId: this.connectionId,
      connectorError: null
    });
  }

  private startClosedWatcher(): void {
    this.closedSub = timer(POPUP_CLOSED_POLL_MS, POPUP_CLOSED_POLL_MS).subscribe(() => {
      if (!this.isPopupClosed()) {
        this.sawPopupOpen = true;
        return;
      }
      this.closedSub?.unsubscribe();
      this.closedSub = null;
      if (this.sawPopupOpen) {
        this.scheduleAbandonmentCheck();
      }
    });
  }

  private isPopupClosed(): boolean {
    try {
      return this.popup.closed;
    } catch {
      return false;
    }
  }

  private scheduleAbandonmentCheck(): void {
    this.graceSub = timer(CLOSE_GRACE_MS).subscribe(() => this.confirmAbandonment());
  }

  private confirmAbandonment(): void {
    if (this.settled || !this.connectionId) {
      return;
    }
    this.cancelSub = this.connectorsApi
      .getAuthorizationStatus(this.connectionId)
      .subscribe({
        next: status => {
          if (status.terminal) {
            this.settle({
              status: status.status,
              connectionId: status.connectionId,
              connectorError: status.connectorError
            });
            return;
          }
          this.cancel();
        },
        error: () => this.cancel()
      });
  }

  private cancel(): void {
    const connectionId = this.connectionId as string;
    const settleCancelled = (): void =>
      this.settle({
        status: EnumOAuthAuthorizationStatus.Cancelled,
        connectionId,
        connectorError: null
      });

    this.cancelSub = this.connectorsApi.cancelAuthorization(connectionId).subscribe({
      next: settleCancelled,
      error: settleCancelled
    });
  }

  private settle(result: IOauthResult): void {
    if (this.settled) {
      return;
    }
    this.settled = true;
    this.sink?.next(result);
    this.sink?.complete();
  }
}
